import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Star as PointsIcon,
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Schedule as TimeIcon,
  Group as TeamIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { ActivitySubmissionResult, User } from '../../types';
import { pointsService } from '../../services/pointsService';
import { userService } from '../../services/userService';
import { activityService } from '../../services/activityService';
import { hasMilesOrMinutes } from '../../utils/activityUtils';

interface ActivitySummaryModalProps {
  open: boolean;
  onClose: () => void;
  onNavigateToDashboard?: () => void; // Callback to navigate to dashboard
  submissionResult: ActivitySubmissionResult;
  teamId: string;
  userId: string;
}

const ActivitySummaryModal: React.FC<ActivitySummaryModalProps> = ({
  open,
  onClose,
  onNavigateToDashboard,
  submissionResult,
  teamId,
  userId
}) => {
  const [userPoints, setUserPoints] = useState<any>(null);
  const [teamRanking, setTeamRanking] = useState<any[]>([]);
  const [userRank, setUserRank] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalDailyPoints, setTotalDailyPoints] = useState<number>(0);

  useEffect(() => {
    if (open) {
      loadSummaryData();
    }
  }, [open, teamId, userId]);

  const loadSummaryData = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('[ActivitySummary] Starting to load summary data...');
      
      // Add timeout to prevent hanging on iOS
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Loading timed out')), 10000); // 10 second timeout
      });

      // Get user's current points with timeout
      console.log('[ActivitySummary] Fetching user points for userId:', userId, 'teamId:', teamId);
      const pointsPromise = pointsService.getUserPoints(userId, teamId);
      const points = await Promise.race([pointsPromise, timeoutPromise]) as any;
      console.log('[ActivitySummary] User points loaded:', points);
      setUserPoints(points);

      // Get today's date to calculate total daily points
      const today = activityService.getTodayDateString();
      console.log('[ActivitySummary] Fetching activities for today:', today);
      
      // Fetch all activities for today to calculate total points
      const todayActivitiesPromise = activityService.getUserActivitiesForDate(userId, today, teamId);
      const todayActivities = await Promise.race([todayActivitiesPromise, timeoutPromise]) as any[];
      console.log('[ActivitySummary] Today activities loaded:', todayActivities?.length || 0, 'activities');
      
      // Calculate total points for today
      // In the new system, points are awarded based on:
      // - Health metrics: 0.5 (by 8 AM) or 0.25 (8 AM-3 PM) or 0 (after 3 PM)
      // - Activity: 0.5 (by 8 AM) or 0.25 (8 AM-3 PM) or 0 (after 3 PM)
      // We need to check what was actually awarded by looking at the points service
      // For now, let's use the walletPointsEarned from recent transactions or calculate from activities
      
      // Calculate total points for today
      // Strategy: Check transactions first (most accurate), then calculate from activities as fallback
      let calculatedTotal = 0;
      const calculateFromTodayActivities = () => {
        if (!todayActivities || todayActivities.length === 0) {
          return submissionResult.pointsEarned || 0;
        }

        let hasHealthMetrics = false;
        let hasActivity = false;
        let earliestSubmission: Date | null = null;

        todayActivities.forEach((activity: any) => {
          if (activity.sleepScore > 0 || activity.hydrationLevel > 0 || activity.nutritionLevel > 0) {
            hasHealthMetrics = true;
          }

          if (hasMilesOrMinutes(activity)) {
            hasActivity = true;
          }

          if (activity.submittedAt) {
            const submittedAt = activity.submittedAt.toDate ? activity.submittedAt.toDate() : new Date(activity.submittedAt);
            if (!earliestSubmission || submittedAt < earliestSubmission) {
              earliestSubmission = submittedAt;
            }
          }
        });

        if (!earliestSubmission) {
          return submissionResult.pointsEarned || 0;
        }

        const timingPoints = pointsService.calculateTimingBasedPoints(
          earliestSubmission,
          today,
          hasHealthMetrics,
          hasActivity
        );
        console.log(
          '[ActivitySummary] Calculated total from activities:',
          timingPoints.walletPoints,
          'hasHealth:',
          hasHealthMetrics,
          'hasActivity:',
          hasActivity
        );
        return timingPoints.walletPoints;
      };
      
      try {
        // Try to get transactions for today
        const recentTransactions = await pointsService.getUserPointsTransactions(userId, teamId, 20);
        const todayTransactions = recentTransactions.filter(t => {
          const transactionDate = t.createdAt?.toDate ? t.createdAt.toDate().toISOString().split('T')[0] : '';
          return transactionDate === today && t.source === 'daily_logging' && t.points > 0;
        });
        
        const totalPointsFromTransactions = todayTransactions.reduce((sum, t) => sum + t.points, 0);
        console.log('[ActivitySummary] Total points from today transactions:', totalPointsFromTransactions);
        
        if (totalPointsFromTransactions > 0) {
          calculatedTotal = totalPointsFromTransactions;
        } else {
          calculatedTotal = calculateFromTodayActivities();
        }
      } catch (transError) {
        console.warn('[ActivitySummary] Could not fetch transactions, calculating from activities:', transError);
        calculatedTotal = calculateFromTodayActivities();
      }
      
      setTotalDailyPoints(calculatedTotal);

      // Get team ranking with timeout
      console.log('[ActivitySummary] Fetching team leaderboard for teamId:', teamId);
      const leaderboardPromise = pointsService.getTeamLeaderboard(teamId);
      const teamLeaderboard = await Promise.race([leaderboardPromise, timeoutPromise]) as any;
      console.log('[ActivitySummary] Team leaderboard loaded:', teamLeaderboard?.length, 'members');
      
      if (teamLeaderboard && teamLeaderboard.length > 0) {
        const sortedMembers = teamLeaderboard.sort((a: any, b: any) => (b.points || 0) - (a.points || 0));
        setTeamRanking(sortedMembers);

        // Find user's rank
        const userIndex = sortedMembers.findIndex((member: any) => member.userId === userId);
        setUserRank(userIndex + 1);
        console.log('[ActivitySummary] User rank:', userIndex + 1, 'out of', sortedMembers.length);
      } else {
        console.log('[ActivitySummary] No team members found in leaderboard');
        setTeamRanking([]);
        setUserRank(0);
      }

    } catch (err: any) {
      console.error('[ActivitySummary] Error loading summary data:', err);
      console.error('[ActivitySummary] Error message:', err.message);
      console.error('[ActivitySummary] Error stack:', err.stack);
      
      setError(err.message === 'Loading timed out' 
        ? 'Loading is taking longer than expected. You can close this and check the dashboard.' 
        : 'Failed to load summary data');
      
      // Set default values on error so modal still shows
      if (!userPoints) {
        console.log('[ActivitySummary] Setting default user points');
        setUserPoints({ points: submissionResult.pointsEarned, availablePoints: submissionResult.pointsEarned });
      }
      if (teamRanking.length === 0) {
        console.log('[ActivitySummary] Setting empty team ranking');
        setTeamRanking([]);
        setUserRank(0);
      }
    } finally {
      console.log('[ActivitySummary] Finished loading, setting loading=false');
      setLoading(false);
    }
  };

  const getTimingMessage = (timing: string) => {
    switch (timing) {
      case 'same_day':
        return 'Excellent timing! You submitted before 8:00 PM.';
      case 'early_next':
        return 'Good timing! You submitted before 8:00 AM the next day.';
      case 'late_next':
        return 'You submitted before 12:00 PM the next day.';
      case 'no_points':
        return 'You submitted after 12:00 PM the next day.';
      default:
        return 'Activity logged successfully.';
    }
  };

  const getTimingColor = (timing: string) => {
    switch (timing) {
      case 'same_day':
        return 'success';
      case 'early_next':
        return 'warning';
      case 'late_next':
        return 'info';
      case 'no_points':
        return 'error';
      default:
        return 'default';
    }
  };

  const displayedPoints = totalDailyPoints > 0 ? totalDailyPoints : submissionResult.pointsEarned;
  const zeroPointsDespiteGoodTiming =
    displayedPoints <= 0 &&
    submissionResult.submissionTiming !== 'no_points';
  const timingChipLabel = zeroPointsDespiteGoodTiming
    ? 'No additional points awarded (already earned for this entry type today).'
    : getTimingMessage(submissionResult.submissionTiming);
  const timingChipColor = zeroPointsDespiteGoodTiming
    ? 'warning'
    : getTimingColor(submissionResult.submissionTiming);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <TrophyIcon color="warning" />;
    if (rank <= 3) return <FireIcon color="error" />;
    if (rank <= 5) return <TrendingUpIcon color="primary" />;
    return <CheckIcon color="action" />;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'warning';
    if (rank <= 3) return 'error';
    if (rank <= 5) return 'primary';
    return 'default';
  };

  const getRankLabel = (rank: number) => {
    if (rank === 1) return '1st Place';
    if (rank === 2) return '2nd Place';
    if (rank === 3) return '3rd Place';
    return `${rank}th Place`;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your summary...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            🎉 Daily Activity Complete!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Here's your summary for today
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Points Earned Today */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" gutterBottom>
                +{displayedPoints} Points
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Earned Today
              </Typography>
              <Chip
                label={timingChipLabel}
                color={timingChipColor as any}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Total Points */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PointsIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Total Points</Typography>
                </Box>
                <Typography variant="h3" color="primary" gutterBottom>
                  {userPoints?.points || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All time points earned
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Team Rank */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getRankIcon(userRank)}
                  <Typography variant="h6" sx={{ ml: 1 }}>Team Rank</Typography>
                </Box>
                <Typography variant="h3" color={getRankColor(userRank) as any} gutterBottom>
                  #{userRank}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getRankLabel(userRank)} out of {teamRanking.length} team members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Team Leaderboard */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TeamIcon sx={{ mr: 1 }} />
              Team Leaderboard
            </Typography>
            
            {teamRanking.length === 0 ? (
              <Alert severity="info">
                No team members found.
              </Alert>
            ) : (
              <List>
                {teamRanking.slice(0, 10).map((member, index) => (
                  <React.Fragment key={member.userId}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getRankIcon(index + 1)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="div">
                              #{index + 1} {member.displayName}
                            </Typography>
                            {member.userId === userId && (
                              <Chip 
                                label="You" 
                                color="primary" 
                                size="small" 
                                sx={{ fontWeight: 'bold' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {member.points || 0} points
                            </Typography>
                            {member.availablePoints !== undefined && (
                              <Typography variant="body2" color="text.secondary" component="span">
                                ({member.availablePoints} available)
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ minWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={teamRanking.length > 0 ? ((member.points || 0) / (teamRanking[0].points || 1)) * 100 : 0}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </ListItem>
                    {index < Math.min(teamRanking.length, 10) - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Motivational Message */}
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {submissionResult.pointsEarned > 0 
              ? `Great job! You're building a consistent habit and earning points for your dedication.`
              : `Thanks for logging your activity! Remember, consistency is key - try to submit before 8:00 PM for maximum points.`
            }
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={() => {
            if (onNavigateToDashboard) {
              onNavigateToDashboard();
            }
            onClose();
          }} 
          variant="contained" 
          size="large" 
          fullWidth
        >
          View Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivitySummaryModal;
